import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useSEO } from "../utils/seo";
import { ProjectContinuity } from "../components/ProjectContinuity";
import { CopyDetails } from "../components/CopyDetails";

const managedServices = [
  "Site control, lease and option negotiations",
  "Easement negotiations and right-of-way acquisition",
  "Land acquisition and landowner coordination",
  "Title, abstracting, ownership research, and curative support",
  "GIS mapping, tract tracking, supplied survey/engineering and authorized field-data integration",
  "Permitting and road/rail/waterway/utility crossing coordination",
  "Independent appraisal coordination and documented acquisition-budget support",
  "Project management, project controls and reporting",
  "Field coordination, construction support, and damage negotiations",
  "Survey coordination and support with licensed providers",
];

const intelligenceServices = [
  "GIS and parcel intelligence",
  "Ownership and title-risk organization",
  "Constraint and evidence review",
  "Structured project data, maps, and scoped reporting",
  "Early comparison of routes, areas, and land-work priorities",
];

const contractRoles = [
  "Right-of-way and acquisition agents",
  "Renewable site-control and landowner-negotiation professionals",
  "Title, abstracting, and ownership-research professionals",
  "Project managers and field coordinators",
  "GIS, records, document-control, and project-controls support",
  "Other land professionals matched to the client, geography, and project",
];

const Solutions = () => {
  useSEO({
    title: "Solutions | Onward Land Solutions",
    description:
      "Managed land projects, corridor/site intelligence and contract professionals, with title, crossing/permit coordination, appraisal coordination and GIS data integration.",
    canonicalPath: "/solutions",
  });

  return (
    <>
      <style>{`
        .sol-hero { background: #090A0C; color: #F5F2EC; padding: 120px 24px 88px; border-bottom: 1px solid rgba(245,242,236,0.08); }
        .sol-hero__inner { max-width: 1200px; margin: 0 auto; }
        .sol-eb { display: inline-block; font-size: 11.5px; font-weight: 700; letter-spacing: 2.4px; text-transform: uppercase; color: #C8102E; margin-bottom: 16px; }
        .sol-h1 { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: clamp(34px, 4.6vw, 56px); line-height: 1.05; letter-spacing: -0.01em; margin: 0 0 20px 0; max-width: 22ch; color: #F5F2EC; }
        .sol-hero__lede { font-size: 17px; line-height: 1.65; color: #DCE1EB; max-width: 68ch; margin: 0; }

        .sol-nav { border-bottom: 1px solid rgba(245,242,236,0.08); background: #0E0F14; }
        .sol-nav__inner { max-width: 1200px; margin: 0 auto; padding: 18px 24px; display: flex; flex-wrap: wrap; gap: 18px 36px; align-items: center; justify-content: center; }
        .sol-nav__item { display: inline-flex; align-items: center; gap: 10px; color: #DCE1EB; font-size: 13px; font-weight: 600; letter-spacing: 0.4px; text-transform: uppercase; text-decoration: none; padding: 6px 2px; border-bottom: 1px solid transparent; transition: color 160ms, border-color 160ms; }
        .sol-nav__item:hover, .sol-nav__item:focus-visible { color: #F5F2EC; border-color: #C8102E; outline: none; }
        .sol-nav__num { font-family: 'Fraunces', Georgia, serif; font-size: 15px; font-weight: 500; color: #E31837; letter-spacing: 0; text-transform: none; }

        .sol-section { padding: 96px 24px; background: #15171B; color: #F5F2EC; }
        .sol-section--black { background: #090A0C; }
        .sol-wrap { max-width: 1200px; margin: 0 auto; }
        .sol-section__num { font-family: 'Fraunces', Georgia, serif; font-size: 15px; font-weight: 500; color: #E31837; letter-spacing: 0.6px; margin-bottom: 4px; }
        .sol-section__eb { display: inline-block; font-size: 11.5px; font-weight: 700; letter-spacing: 2.4px; text-transform: uppercase; color: #C8102E; margin-bottom: 14px; }
        .sol-h2 { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: clamp(28px, 3.4vw, 42px); line-height: 1.1; letter-spacing: -0.01em; margin: 0 0 16px 0; color: #F5F2EC; max-width: 24ch; }
        .sol-h3 { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: clamp(20px, 1.8vw, 24px); line-height: 1.2; margin: 0 0 8px 0; color: #F5F2EC; }
        .sol-lede { font-size: 16.5px; line-height: 1.65; color: #DCE1EB; max-width: 68ch; margin: 0 0 40px 0; }
        .sol-lede--tight { margin-bottom: 28px; }

        .sol-services { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 32px; margin: 0 0 40px 0; padding: 0; list-style: none; }
        .sol-services li { display: flex; align-items: flex-start; gap: 12px; font-size: 15px; line-height: 1.55; color: #DCE1EB; padding: 12px 0; border-bottom: 1px solid rgba(245,242,236,0.08); }
        .sol-services li::before { content: ""; display: inline-block; width: 6px; height: 6px; background: #C8102E; margin-top: 8px; flex-shrink: 0; transform: rotate(45deg); }

        .sol-products { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 36px; }
        .sol-prod { background: #090A0C; border: 1px solid rgba(245,242,236,0.10); border-left: 3px solid #C8102E; padding: 28px; border-radius: 4px; display: flex; flex-direction: column; gap: 12px; transition: border-color 200ms, transform 200ms; }
        .sol-prod:hover { border-color: rgba(245,242,236,0.20); border-left-color: #E31837; transform: translateY(-2px); }
        .sol-prod__head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
        .sol-prod__name { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: 26px; color: #F5F2EC; margin: 0; line-height: 1.1; }
        .sol-prod__badge { display: inline-flex; align-items: center; gap: 6px; padding: 4px 10px; border: 1px solid #E31837; color: #F5F2EC; background: rgba(200,16,46,0.14); border-radius: 999px; font-size: 10.5px; font-weight: 700; letter-spacing: 1.4px; text-transform: uppercase; }
        .sol-prod__badge::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: #E31837; }
        .sol-prod__body { font-size: 14.5px; line-height: 1.6; color: #DCE1EB; margin: 0; }
        .sol-prod__fit { font-size: 13px; line-height: 1.55; color: #B8BFCB; margin: 0; }
        .sol-prod__fit strong { color: #F5F2EC; font-weight: 700; letter-spacing: 0.4px; text-transform: uppercase; font-size: 11px; }
        .sol-prod__link { display: inline-flex; align-items: center; gap: 6px; color: #E31837; font-size: 14px; font-weight: 600; text-decoration: none; margin-top: 4px; align-self: flex-start; border-bottom: 1px solid rgba(227,24,55,0.35); padding-bottom: 2px; transition: color 160ms, border-color 160ms; }
        .sol-prod__link:hover, .sol-prod__link:focus-visible { color: #F5F2EC; border-color: #F5F2EC; outline: none; }

        .sol-guardrail { margin-top: 28px; padding: 18px 22px; background: rgba(245,242,236,0.04); border-left: 3px solid rgba(245,242,236,0.20); font-size: 13.5px; line-height: 1.6; color: #B8BFCB; border-radius: 2px; max-width: 68ch; }

        .sol-cta-row { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 12px; }
        .sol-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; font-size: 14px; font-weight: 700; letter-spacing: 0.3px; border-radius: 4px; text-decoration: none; transition: background 180ms, color 180ms, border-color 180ms, transform 180ms, box-shadow 180ms; border: 1px solid transparent; cursor: pointer; }
        .sol-btn--fill { background: #C8102E; color: #ffffff; border-color: #C8102E; box-shadow: 0 6px 22px rgba(200,16,46,0.32); }
        .sol-btn--fill:hover, .sol-btn--fill:focus-visible { background: #E31837; border-color: #E31837; transform: translateY(-1px); box-shadow: 0 10px 30px rgba(227,24,55,0.42); outline: none; }
        .sol-btn--outline { background: transparent; color: #F5F2EC; border-color: #F5F2EC; }
        .sol-btn--outline:hover, .sol-btn--outline:focus-visible { background: #F5F2EC; color: #090A0C; outline: none; }
        .sol-btn--outline-crimson { background: transparent; color: #F5F2EC; border-color: #E31837; }
        .sol-btn--outline-crimson:hover, .sol-btn--outline-crimson:focus-visible { background: #C8102E; color: #ffffff; border-color: #C8102E; outline: none; }

        .sol-final { background: #090A0C; padding: 96px 24px; text-align: center; border-top: 1px solid rgba(245,242,236,0.08); }
        .sol-final__inner { max-width: 900px; margin: 0 auto; }
        .sol-final h2 { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: clamp(28px, 3.6vw, 44px); line-height: 1.1; margin: 0 0 18px 0; color: #F5F2EC; }
        .sol-final p { font-size: 16px; line-height: 1.65; color: #DCE1EB; margin: 0 0 30px 0; }
        .sol-final .sol-cta-row { justify-content: center; }

        @media (max-width: 900px) {
          .sol-services { grid-template-columns: 1fr; }
          .sol-products { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .sol-hero { padding: 96px 20px 64px; }
          .sol-section { padding: 72px 20px; }
          .sol-final { padding: 72px 20px; }
          .sol-nav__inner { justify-content: flex-start; gap: 14px 22px; padding: 16px 20px; }
        }
      `}</style>

      {/* HERO */}
      <section className="sol-hero" data-testid="solutions-hero">
        <div className="sol-hero__inner">
          <span className="sol-eb" data-testid="solutions-eyebrow">Solutions</span>
          <h1 className="sol-h1" data-testid="solutions-headline">
            Land work, at the level you need.
          </h1>
          <p className="sol-hero__lede" data-testid="solutions-lede">
            Managed projects, corridor and area intelligence, or contract professionals for your team.
          </p>
        </div>
      </section>

      {/* IN-PAGE NAV: three business lines visible at a glance */}
      <nav className="sol-nav" aria-label="Business lines" data-testid="solutions-nav">
        <div className="sol-nav__inner">
          <a href="#managed" className="sol-nav__item" data-testid="solutions-nav-managed">
            <span className="sol-nav__num">01</span> Managed Land Projects
          </a>
          <a href="#intelligence" className="sol-nav__item" data-testid="solutions-nav-intelligence">
            <span className="sol-nav__num">02</span> Land & Corridor Intelligence
          </a>
          <a href="#contract" className="sol-nav__item" data-testid="solutions-nav-contract">
            <span className="sol-nav__num">03</span> Contract Land Professionals
          </a>
        </div>
      </nav>

      {/* 01 MANAGED LAND PROJECTS */}
      <section id="managed" className="sol-section" data-testid="solutions-section-managed">
        <div className="sol-wrap">
          <p className="sol-section__num">01</p>
          <span className="sol-section__eb">Managed Land Projects</span>
          <h2 className="sol-h2">One accountable team.</h2>
          <p className="sol-lede">
            Site control, title, acquisition, permitting/crossing coordination, appraisal coordination and acquisition-budget support. GIS integrates supplied survey/engineering and authorized field data.
          </p>

          <CopyDetails id="solutions-managed" title="Managed project services">
          <ul className="sol-services" data-testid="solutions-managed-services">
            {managedServices.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          </CopyDetails>

          <div className="sol-cta-row">
            <Link
              to="/contact?source=solutions_managed"
              className="sol-btn sol-btn--fill"
              data-testid="solutions-cta-managed"
            >
              Talk to Onward <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 02 LAND & CORRIDOR INTELLIGENCE */}
      <section id="intelligence" className="sol-section sol-section--black" data-testid="solutions-section-intelligence">
        <div className="sol-wrap">
          <p className="sol-section__num">02</p>
          <span className="sol-section__eb">Land & Corridor Intelligence</span>
          <h2 className="sol-h2">Clarify the land decision.</h2>
          <p className="sol-lede sol-lede--tight">
            Professional judgment, supported by mapping and agreed sources.
          </p>

          <div className="sol-products" data-testid="solutions-products">
            {/* PathScore */}
            <article className="sol-prod" data-testid="solutions-product-pathscore">
              <div className="sol-prod__head">
                <h3 className="sol-prod__name">PathScore</h3>
              </div>
              <p className="sol-prod__body">
                Complimentary PathScore First Look, then scoped route evaluation, professional selected-route land/title work and an execution plan.
              </p>
              <p className="sol-prod__fit">
                <strong>Best fit:</strong> Linear infrastructure.
              </p>
              <Link to="/path-score?source=solutions_intelligence" className="sol-prod__link" data-testid="solutions-link-pathscore">
                See What Full PathScore Delivers <ChevronRight className="h-4 w-4" />
              </Link>
              <Link to="/score-a-corridor?source=solutions_first_look" className="sol-prod__link" data-testid="solutions-first-look-cta"><span>Get a Complimentary PathScore First Look</span><ChevronRight className="h-4 w-4 shrink-0" /></Link>
              <p className="sol-prod__fit" data-testid="solutions-first-look-scope">Complimentary preliminary screening. Full PathScore is separately scoped paid work.</p>
            </article>

            {/* AreaScore */}
            <article className="sol-prod" data-testid="solutions-product-areascore">
              <div className="sol-prod__head">
                <h3 className="sol-prod__name">AreaScore</h3>
              </div>
              <p className="sol-prod__body">
                Scoped area/site review of land, access and infrastructure proximity.
              </p>
              <p className="sol-prod__fit">
                <strong>Best fit:</strong> Land-based development.
              </p>
              <Link to="/area-score?source=solutions_intelligence" className="sol-prod__link" data-testid="solutions-link-areascore">
                Explore AreaScore <ChevronRight className="h-4 w-4" />
              </Link>
            </article>
          </div>

          <p className="sol-guardrail" data-testid="solutions-guardrail">
            Screening is not project approval or confirmation of capacity or availability. Qualified independent appraisers provide formal valuation. Onward coordinates survey access and licensed providers, not licensed surveying or utility locating.
          </p>

          <CopyDetails id="solutions-intelligence" title="Supporting intelligence capabilities">
          <ul className="sol-services" data-testid="solutions-intelligence-services" style={{ marginBottom: 12 }}>
            {intelligenceServices.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>

          </CopyDetails>
          <div className="sol-cta-row">
            <Link
              to="/contact?source=solutions_intelligence_cta"
              className="sol-btn sol-btn--fill"
              data-testid="solutions-cta-intelligence"
            >
              Talk to Onward <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* 03 CONTRACT LAND PROFESSIONALS */}
      <section id="contract" className="sol-section" data-testid="solutions-section-contract">
        <div className="sol-wrap">
          <p className="sol-section__num">03</p>
          <span className="sol-section__eb">Contract Land Professionals</span>
          <h2 className="sol-h2">Staff to the workload.</h2>
          <p className="sol-lede">
            Supplement your team with qualified land professionals. Interest and availability are confirmed for each engagement.
          </p>

          <CopyDetails id="solutions-roles" title="Roles and specialties">
          <ul className="sol-services" data-testid="solutions-contract-roles">
            {contractRoles.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
          </CopyDetails>

          <div className="sol-cta-row">
            <Link
              to="/talent-network?view=client&source=solutions_contract#client-request"
              className="sol-btn sol-btn--fill"
              data-testid="solutions-cta-contract"
            >
              Request Contract Land Help <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/talent-network?view=professional&source=solutions_talent#professional-application"
              className="sol-btn sol-btn--outline-crimson"
              data-testid="solutions-cta-talent-pool"
            >
              Join the Talent Pool <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="sol-section sol-section--black" data-testid="solutions-continuity-section">
        <div className="sol-wrap"><ProjectContinuity id="solutions" /></div>
      </section>

      {/* CLOSER */}
      <section className="sol-final" data-testid="solutions-final">
        <div className="sol-final__inner">
          <h2>Tell us where the project stands.</h2>
          <p>
            Share the need, location and timing.
          </p>
          <div className="sol-cta-row">
            <Link to="/contact?source=solutions_final" className="sol-btn sol-btn--fill" data-testid="solutions-final-cta-primary">
              Talk to Onward <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/talent-network?view=client&source=solutions_final#client-request" className="sol-btn sol-btn--outline" data-testid="solutions-final-cta-secondary">
              Request Contract Land Help <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Solutions;
